import { AgentRegistryIcon } from '@/components/icons/agent-registry-icon'
import { ChartColumnIcon } from '@/components/icons/chart-column-icon'
import { DataCatalogIcon } from '@/components/icons/data-catalog-icon'
import { HistoryIcon } from '@/components/icons/history-icon'
import { ScheduleIcon } from '@/components/icons/schedule-icon'
import { ServiceRegistryIcon } from '@/components/icons/service-registry-icon'
import { SettingsIcon } from '@/components/icons/settings-icon'
import { TableIcon } from '@/components/icons/table-icon'
import { cn } from '@/lib/utils'
import {
  CirclePlus,
  FileJson,
  Info,
  List,
  MessageCircle,
  MessageCircleMore,
  MessageCirclePlus,
  MonitorPlay,
  Radio,
} from 'lucide-react'
import {
  TAB_TYPE_CHAT,
  TAB_TYPE_DATA_GOVERNANCE_DASHBOARD,
  TAB_TYPE_DATABASE_METADATA,
  TAB_TYPE_DATABASE_SCHEMA,
  TAB_TYPE_DATABASE_TABLE,
  TAB_TYPE_TURN,
  TabType,
  TabTypeStatic,
  TabTypeTurnStep,
} from './types'

type TabMetadata = {
  title: string
  description?: string
}

// =============================================================================
// CHAT TAB METADATA
// =============================================================================

export const TAB_METADATA: Record<TabType, TabMetadata> = {
  [TAB_TYPE_CHAT]: {
    title: 'Chat',
  },
  [TabTypeTurnStep.RecordedSession]: {
    title: 'Recorded Session',
  },
  [TabTypeTurnStep.LiveSession]: {
    title: 'Live Session',
  },
  [TabTypeTurnStep.StepUpdates]: {
    title: 'Step Updates',
  },
  [TabTypeStatic.NewChat]: {
    title: 'New Chat',
    description: 'Start a new chat conversation',
  },
  [TabTypeStatic.Blank]: {
    title: 'New Tab',
    description: 'Empty tab for new content',
  },
  [TabTypeStatic.AgentRegistry]: {
    title: 'Agent Registry',
    description: 'View and manage your Agents',
  },
  [TabTypeStatic.ServiceRegistry]: {
    title: 'Service Registry',
    description: 'View and manage your MCP and other secure services',
  },
  [TabTypeStatic.History]: {
    title: 'Chat History',
    description: 'View and manage your Workflows',
  },
  [TabTypeStatic.Schedule]: {
    title: 'Scheduling',
    description: 'View and manage your Agents’ Schedules',
  },
  [TabTypeStatic.DataCatalog]: {
    title: 'Data Catalog',
    description: 'Manage data assets with metadata management and enrichment',
  },
  [TabTypeStatic.Settings]: {
    title: 'Settings',
    description: 'Configure your account and preferences',
  },
  [TabTypeStatic.IndustryReference]: {
    title: 'Industry Reference',
    description: 'Industry standards and frameworks for metadata enrichment',
  },
  [TAB_TYPE_DATABASE_SCHEMA]: {
    title: 'Database Schema',
  },
  [TAB_TYPE_DATABASE_TABLE]: {
    title: 'Database Table',
  },
  [TAB_TYPE_DATABASE_METADATA]: {
    title: 'Database Metadata',
  },
  [TAB_TYPE_TURN]: {
    title: 'Output Message',
  },
  [TAB_TYPE_DATA_GOVERNANCE_DASHBOARD]: {
    title: 'Data Governance Dashboard',
  },
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Get title for a tab type
 */
export const getTabMetadata = (type: TabType): TabMetadata => TAB_METADATA[type]

// =============================================================================
// TAB ICONS
// =============================================================================

const TAB_ICON_MAP: Record<TabType, React.FC<React.SVGProps<SVGSVGElement>>> = {
  [TAB_TYPE_TURN]: MessageCircleMore,
  [TAB_TYPE_CHAT]: MessageCircle,
  [TabTypeStatic.NewChat]: MessageCirclePlus,
  [TabTypeStatic.AgentRegistry]: AgentRegistryIcon,
  [TabTypeStatic.ServiceRegistry]: ServiceRegistryIcon,
  [TabTypeStatic.History]: HistoryIcon,
  [TabTypeStatic.Schedule]: ScheduleIcon,
  [TabTypeStatic.DataCatalog]: DataCatalogIcon,
  [TabTypeTurnStep.LiveSession]: Radio,
  [TabTypeStatic.Settings]: SettingsIcon,
  [TabTypeStatic.IndustryReference]: Info,
  [TAB_TYPE_DATA_GOVERNANCE_DASHBOARD]: ChartColumnIcon,
  [TAB_TYPE_DATABASE_SCHEMA]: TableIcon,
  [TAB_TYPE_DATABASE_TABLE]: TableIcon,
  [TAB_TYPE_DATABASE_METADATA]: FileJson,
  [TabTypeStatic.Blank]: ({ style, ...rest }) => (
    <CirclePlus style={{ ...style, width: 17, height: 17 }} {...rest} />
  ),
  [TabTypeTurnStep.StepUpdates]: ({ style, ...rest }: React.SVGProps<SVGSVGElement>) => (
    <List style={{ ...style, width: 18, height: 18 }} {...rest} />
  ),
  [TabTypeTurnStep.RecordedSession]: ({ style, ...rest }: React.SVGProps<SVGSVGElement>) => (
    <MonitorPlay style={{ ...style, width: 17, height: 17 }} {...rest} />
  ),
}

type TabIconProps = Readonly<{ type: TabType } & React.SVGProps<SVGSVGElement>>

export function TabIcon({ type, className, ...rest }: TabIconProps) {
  const Icon = TAB_ICON_MAP[type]
  return (
    <Icon strokeWidth={1} className={cn('size-5 shrink-0 text-neutral-150', className)} {...rest} />
  )
}
